// stage-slider.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
import Flickity from "flickity";

class StageSlider {
    constructor(elementOrSelector) {
        // Selector ODER Element akzeptieren
        this.root = typeof elementOrSelector === 'string'
            ? document.querySelector(elementOrSelector)
            : elementOrSelector;

        if (!this.root) {
            console.warn('StageSlider: root element not found.', elementOrSelector);
            return; // früh raus, kein Fehler mehr
        }

        this.carouselEl = this.root.querySelector(".carousel");
        this.dotsWrap   = this.root.querySelector(".js-stage-dots");

        if (!this.carouselEl) {
            console.warn('StageSlider: .carousel not found within root.', this.root);
            return;
        }
        if (!this.dotsWrap) {
            console.warn('StageSlider: .js-stage-dots not found within root.', this.root);
            return;
        }

        const SLIDE_DURATION = 3; // Sek.

        this.flkty = new Flickity(this.carouselEl, {
            adaptiveHeight: false,
            cellAlign: "left",
            draggable: true,
            pageDots: false,
            prevNextButtons: false,
            wrapAround: true,
            autoPlay: false,
            initialIndex: 0,
        });

        // --- Dots bauen -------------------------------------------------
        const cellCount = this.flkty.cells.length;
        this.dotBars = [];

        for (let i = 0; i < cellCount; i++) {
            // Button statt div (type wirkt sonst nicht)
            const dot = document.createElement("button");
            dot.className = "dot";
            dot.type = "button";
            dot.setAttribute("aria-label", `Gehe zu Slide ${i + 1}`);
            dot.dataset.index = i;

            const track = document.createElement("span");
            track.className = "dot__track";
            const bar = document.createElement("span");
            bar.className = "dot__bar";
            bar.style.transformOrigin = "left center";
            bar.style.transform = "scaleX(0)";
            track.appendChild(bar);
            dot.appendChild(track);
            this.dotsWrap.appendChild(dot);
            this.dotBars.push(bar);

            dot.addEventListener("click", () => {
                killProgress();
                this.flkty.select(i, false, false);
            });
        }

        // --- Progress Logik ---------------------------------------------
        let progressTL = null;

        const killProgress = () => {
            if (progressTL) {
                progressTL.kill();
                progressTL = null;
            }
        };

        const resetBarsAndActive = () => {
            this.dotBars.forEach((b) => {
                gsap.set(b, { scaleX: 0 });
                b.closest(".dot")?.classList.remove("is-active");
            });
        };

        const startProgressFor = (index) => {
            killProgress();
            resetBarsAndActive();

            const bar = this.dotBars[index];
            bar?.closest(".dot")?.classList.add("is-active");

            progressTL = gsap.fromTo(
                bar,
                { scaleX: 0 },
                {
                    scaleX: 1,
                    ease: "linear",
                    duration: SLIDE_DURATION,
                    onComplete: () => this.flkty.next(),
                }
            );
        };

        const pauseProgress = () => {
            if (progressTL && progressTL.isActive()) progressTL.pause();
        };
        const resumeProgress = () => {
            if (progressTL && progressTL.paused()) progressTL.resume();
        };

        // --- Flickity Events --------------------------------------------
        this.flkty.on("select", () => {
            startProgressFor(this.flkty.selectedIndex);
        });

        this.flkty.on("dragStart", pauseProgress);
        this.flkty.on("dragEnd", () => {
            resumeProgress(); // select feuert ohnehin
        });

        [this.root, this.dotsWrap].forEach((el) => {
            el.addEventListener("mouseenter", pauseProgress);
            el.addEventListener("mouseleave", resumeProgress);
        });

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) pauseProgress();
            else resumeProgress();
        });

        const init = () => {
            this.flkty.resize();
            startProgressFor(this.flkty.selectedIndex);
            ScrollTrigger.refresh();
        };
        if (document.readyState === "complete") init();
        else window.addEventListener("load", init);

        this.refresh = () => {
            this.flkty.reloadCells();
            this.flkty.resize();
            this.flkty.reposition();
            startProgressFor(this.flkty.selectedIndex);
        };
    }

    // Optionaler Helfer: mountet alle Slider auf der Seite
    static mountAll(selector = '.js-stage-slider') {
        document.querySelectorAll(selector).forEach(el => new StageSlider(el));
    }
}

export default StageSlider;
